/**
 * rendering-registry/resolver.ts
 *
 * Resolution engine — selects the best-matching registry entry for a given
 * instructional context. All four generators call this instead of maintaining
 * their own representation-selection logic.
 *
 * SCORING MODEL
 * ─────────────────────────────────────────────────────────────────────────────
 * Each defined criterion in a match adds points when it matches.
 * A defined criterion that does NOT match disqualifies the entry entirely.
 *
 *   standardPrefixes  +4   (most narrowing — ties to exact standard)
 *   gradeRange        +3
 *   subjects          +2
 *   promptPatterns    +2   (any-match — scored once, not per pattern)
 *   cognitiveOps      +1
 *   conceptDomains    +1
 *
 * The catch-all entry (match: {}) always scores 0 and serves as the floor.
 * The first entry in REGISTRY order wins ties.
 *
 * CONFLICT DETECTION
 * ─────────────────────────────────────────────────────────────────────────────
 * validateRegistry() checks for duplicate match-criteria keys and duplicate IDs.
 * It is called once at module load (see index.ts).
 */

import {
  REGISTRY,
  WS_HEIGHTS_BY_KIND,
} from './registry';

import type {
  RegistryEntry,
  ResolutionContext,
  Resolution,
  ResolvedRepresentation,
  WorkspaceKind,
  WorkspaceSize,
} from './types';

import { RegistryConflictError } from './types';

// ── Scoring ────────────────────────────────────────────────────────────────────

function scoreEntry(entry: RegistryEntry, ctx: ResolutionContext): number {
  const { match } = entry;
  let score = 0;

  // Standard prefixes — most specific possible match
  if (match.standardPrefixes !== undefined) {
    const sp = ctx.standardPrefix;
    if (!sp || !match.standardPrefixes.some(p => sp.startsWith(p))) return -Infinity;
    score += 4;
  }

  // Grade range
  if (match.gradeRange !== undefined) {
    if (ctx.grade !== undefined) {
      if (ctx.grade < match.gradeRange[0] || ctx.grade > match.gradeRange[1]) return -Infinity;
      score += 3;
    }
    // grade undefined → criteria cannot be evaluated, don't score or penalize
  }

  // Subjects
  if (match.subjects !== undefined) {
    if (!ctx.subject || !match.subjects.includes(ctx.subject)) return -Infinity;
    score += 2;
  }

  // Cognitive operations (Axis 1)
  if (match.cognitiveOps !== undefined) {
    if (!ctx.cognitiveOp || !match.cognitiveOps.includes(ctx.cognitiveOp)) return -Infinity;
    score += 1;
  }

  // Concept domains (Axis 2)
  if (match.conceptDomains !== undefined) {
    if (!ctx.conceptDomain || !match.conceptDomains.includes(ctx.conceptDomain)) return -Infinity;
    score += 1;
  }

  // Prompt patterns — at least one must match
  if (match.promptPatterns !== undefined) {
    const p = ctx.prompt.toLowerCase();
    const anyMatch = match.promptPatterns.some(pat => new RegExp(pat).test(p));
    if (!anyMatch) return -Infinity;
    score += 2;
  }

  return score; // 0 for the universal catch-all (match: {})
}

// ── Primary resolution ─────────────────────────────────────────────────────────

/**
 * Find the best-matching registry entry for a problem prompt.
 * Returns a ResolvedRepresentation (never throws under normal operation).
 *
 * The computation-default catch-all guarantees a match always exists.
 */
export function resolveForPrompt(ctx: ResolutionContext): ResolvedRepresentation {
  let bestEntry: RegistryEntry = REGISTRY[REGISTRY.length - 1]; // catch-all fallback
  let bestScore = -Infinity;

  for (const entry of REGISTRY) {
    const score = scoreEntry(entry, ctx);
    if (score > bestScore) {
      bestScore = score;
      bestEntry = entry;
    }
  }

  return {
    blocked: false,
    entryId: bestEntry.id,
    representationType: bestEntry.representation.type,
    workspaceKind: bestEntry.activitySheet.workspaceKind,
    wsHeights: bestEntry.activitySheet.wsHeights,
    fallbackUsed: null,
  };
}

// ── Presentation imageHint routing ────────────────────────────────────────────

/**
 * Find the registry entry that owns a given imageHint string.
 * Tests each entry's imageHintPattern in registry order; returns the first match.
 * Returns null if no entry claims the hint — callers fall through to FlowDiagramSVG.
 */
export function resolveForHint(hint: string): RegistryEntry | null {
  if (!hint) return null;
  for (const entry of REGISTRY) {
    const pat = entry.representation.imageHintPattern;
    if (pat && new RegExp(pat, 'i').test(hint)) {
      return entry;
    }
  }
  return null;
}

// ── Fallback chain resolution ─────────────────────────────────────────────────

/**
 * Walk the fallback chain for an entry whose component has failed.
 * Returns the first reachable terminal, or a BlockedResolution if the chain
 * reaches a `block-export` node before finding a terminal.
 *
 * This is used by error-boundary handlers, not in normal rendering paths.
 */
export function resolveWithFallback(
  entryId: string,
  visited: Set<string> = new Set(),
): Resolution {
  if (visited.has(entryId)) {
    // Cycle guard — shouldn't occur if validateRegistry() passes
    return computationFallback(entryId);
  }
  visited.add(entryId);

  const entry = REGISTRY.find(e => e.id === entryId);
  if (!entry) return computationFallback(entryId);

  const fb = entry.fallback;

  if (fb.kind === 'terminal') {
    // fb.workspaceKind is typed as 'computation' | 'lines' — both valid WorkspaceKind and RepresentationType
    const kind = fb.workspaceKind; // 'computation' | 'lines'
    return {
      blocked: false,
      entryId,
      representationType: kind as import('./types').RepresentationType,
      workspaceKind: kind as WorkspaceKind,
      wsHeights: WS_HEIGHTS_BY_KIND[kind],
      fallbackUsed: kind,
    };
  }

  if (fb.kind === 'block-export') {
    return {
      blocked: true,
      entryId,
      message: fb.message,
    };
  }

  // fb.kind === 'entry' — recurse into the next entry
  const child = resolveWithFallback(fb.id, visited);
  if (!child.blocked && child.fallbackUsed === null) {
    return { ...child, fallbackUsed: fb.id };
  }
  return child;
}

function computationFallback(entryId: string): ResolvedRepresentation {
  return {
    blocked: false,
    entryId,
    representationType: 'computation',
    workspaceKind: 'computation',
    wsHeights: WS_HEIGHTS_BY_KIND['computation'],
    fallbackUsed: 'computation-default',
  };
}

// ── Blocked resolution check ──────────────────────────────────────────────────

/** Shape accepted by checkBlockedResolutions — matches ActivityProblem fields. */
export interface ProblemRef {
  number: number;
  _registryEntryId?: string;
}

/** A problem whose entry's fallback chain terminates at block-export. */
export interface BlockedProblemInfo {
  problemNumber: number;
  entryId: string;
  message: string;
}

/**
 * Pre-export safety check.
 *
 * Walks the fallback chain for every problem that has a _registryEntryId stamp.
 * Returns one BlockedProblemInfo per problem whose chain reaches block-export.
 *
 * Call this in EVERY export path (print, PPTX, PDF) before the export begins.
 * If the returned array is non-empty, block the export and surface the messages
 * to the teacher — never silently produce a worksheet with a missing representation.
 *
 * Problems without a _registryEntryId (e.g. saved before v3.1) are skipped.
 */
export function checkBlockedResolutions(problems: ProblemRef[]): BlockedProblemInfo[] {
  const blocked: BlockedProblemInfo[] = [];
  for (const p of problems) {
    if (!p._registryEntryId) continue;
    const r = resolveWithFallback(p._registryEntryId);
    if (r.blocked) {
      blocked.push({ problemNumber: p.number, entryId: r.entryId, message: r.message });
    }
  }
  return blocked;
}

// ── Height lookup ─────────────────────────────────────────────────────────────

/**
 * Look up the workspace height (px) for a given kind and size.
 * Returns 0 for 'none' kind or 'none' size.
 */
export function getWsHeight(kind: WorkspaceKind | string, size: WorkspaceSize): number {
  if (kind === 'none' || size === 'none') return 0;
  const row = WS_HEIGHTS_BY_KIND[kind as WorkspaceKind];
  return row?.[size] ?? 0;
}

// ── Conflict detection ─────────────────────────────────────────────────────────

/**
 * Validate the registry for conflicts. Called once at module load.
 *
 * Checks:
 *   1. No two entries share the same ID.
 *   2. No two entries have identical match criteria (would cause silent shadowing).
 *   3. Every fallback chain terminates (no cycles, no dangling entry references).
 *
 * Throws RegistryConflictError on any violation.
 */
export function validateRegistry(): void {
  // 1. Duplicate IDs
  const idsSeen = new Set<string>();
  for (const entry of REGISTRY) {
    if (idsSeen.has(entry.id)) {
      throw new RegistryConflictError(entry.id, entry.id, 'duplicate entry ID');
    }
    idsSeen.add(entry.id);
  }

  // 2. Identical match criteria
  const keysSeen = new Map<string, string>(); // matchKey → entryId
  for (const entry of REGISTRY) {
    const key = matchKey(entry.match);
    const existing = keysSeen.get(key);
    if (existing) {
      throw new RegistryConflictError(
        entry.id,
        existing,
        `identical match criteria key: "${key}"`,
      );
    }
    keysSeen.set(key, entry.id);
  }

  // 3. Fallback chains terminate and have no cycles
  for (const entry of REGISTRY) {
    validateFallbackChain(entry.id, new Set());
  }
}

function matchKey(m: import('./types').MatchCriteria): string {
  const parts: string[] = [];
  if (m.gradeRange)       parts.push(`gr:${m.gradeRange[0]}-${m.gradeRange[1]}`);
  if (m.subjects)         parts.push(`sub:${[...m.subjects].sort().join(',')}`);
  if (m.cognitiveOps)     parts.push(`cop:${[...m.cognitiveOps].sort().join(',')}`);
  if (m.conceptDomains)   parts.push(`cd:${[...m.conceptDomains].sort().join(',')}`);
  if (m.standardPrefixes) parts.push(`std:${[...m.standardPrefixes].sort().join(',')}`);
  if (m.promptPatterns)   parts.push(`pp:${[...m.promptPatterns].sort().join('|')}`);
  return parts.join('§') || '(catch-all)';
}

function validateFallbackChain(entryId: string, visited: Set<string>): void {
  if (visited.has(entryId)) {
    throw new RegistryConflictError(
      entryId,
      [...visited].find(id => id !== entryId) ?? entryId,
      `fallback cycle detected involving entry "${entryId}"`,
    );
  }

  const entry = REGISTRY.find(e => e.id === entryId);
  if (!entry) {
    // Dangling reference — an entry points to a non-existent fallback
    throw new Error(
      `Registry entry fallback references unknown id "${entryId}". ` +
      `Check the fallback chain in registry.ts.`,
    );
  }

  const fb = entry.fallback;
  if (fb.kind === 'terminal' || fb.kind === 'block-export') return; // chain ends here

  // fb.kind === 'entry' — recurse
  visited.add(entryId);
  validateFallbackChain(fb.id, new Set(visited));
}
